import { Autocomplete, Dialog, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { EvaluationType } from '@atlas/shared';
import { rankOptions, type Product } from '@atlas/shared';
import { useProductOptions } from '@/hooks/useProductOptions';
import { getEvaluationTypeShortLabel } from '@/utils/evaluationTypeLabels';

interface NewEvaluationDialogProps {
  open: boolean;
  onClose: () => void;
  /** Defaults to hardware, which needs no `evaluationType` param. */
  evaluationType?: EvaluationType;
}

/**
 * Asks for the product before starting an evaluation — CreateEvaluationPage
 * refuses to start without `?productId`. Shared by the Evaluations page and the
 * sidebar's New menu. Labels are hardcoded English, to avoid regenerating 9
 * locales for two strings.
 */
export function NewEvaluationDialog({ open, onClose, evaluationType = 'hardware' }: NewEvaluationDialogProps) {
  const navigate = useNavigate();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New {getEvaluationTypeShortLabel(evaluationType)}</DialogTitle>
      <DialogContent>
        {open && (
          <ProductPicker
            onPick={(productId) => {
              onClose();
              navigate(
                `/evaluations/new?productId=${encodeURIComponent(productId)}${
                  evaluationType !== 'hardware' ? `&evaluationType=${evaluationType}` : ''
                }`,
              );
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

const pickerLabel = (p: Product) => (p.metadata?.name as string) ?? p.productId;

/** Its own component so the ~1,500-row product list only loads once the dialog opens. */
function ProductPicker({ onPick }: { onPick: (productId: string) => void }) {
  const { products, isLoading } = useProductOptions();
  return (
    <Autocomplete
      options={products}
      loading={isLoading}
      getOptionKey={(p) => p.productId}
      getOptionLabel={pickerLabel}
      filterOptions={(opts, state) => rankOptions(opts, state.inputValue, pickerLabel)}
      onChange={(_e, p) => p && onPick(p.productId)}
      autoHighlight
      openOnFocus
      renderInput={(params) => (
        <TextField {...params} autoFocus size="small" label="Product" sx={{ mt: 1 }} />
      )}
    />
  );
}
